import type { Rect, Vec } from './types.js';

export const EPSILON = 1e-9;

// ---------------------------------------------------------------------------
// Vectors
// ---------------------------------------------------------------------------

export const vec = (x: number, y: number): Vec => ({ x, y });
export const vAdd = (a: Vec, b: Vec): Vec => ({ x: a.x + b.x, y: a.y + b.y });
export const vSub = (a: Vec, b: Vec): Vec => ({ x: a.x - b.x, y: a.y - b.y });
export const vMul = (a: Vec, k: number): Vec => ({ x: a.x * k, y: a.y * k });
export const vDot = (a: Vec, b: Vec): number => a.x * b.x + a.y * b.y;
export const vCross = (a: Vec, b: Vec): number => a.x * b.y - a.y * b.x;
export const vLen = (a: Vec): number => Math.hypot(a.x, a.y);
export const vDist = (a: Vec, b: Vec): number => Math.hypot(a.x - b.x, a.y - b.y);
export const vLerp = (a: Vec, b: Vec, t: number): Vec => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
});

export function vNorm(a: Vec): Vec {
  const l = vLen(a);
  return l < EPSILON ? { x: 0, y: 0 } : { x: a.x / l, y: a.y / l };
}

/** Perpendicular, rotated 90 degrees clockwise in screen coordinates. */
export const vPerp = (a: Vec): Vec => ({ x: -a.y, y: a.x });

export function vRotate(p: Vec, origin: Vec, angle: number): Vec {
  if (angle === 0) return { x: p.x, y: p.y };
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const dx = p.x - origin.x;
  const dy = p.y - origin.y;
  return { x: origin.x + dx * c - dy * s, y: origin.y + dx * s + dy * c };
}

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Shortest signed angular difference, in (-PI, PI]. */
export function angleDelta(from: number, to: number): number {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d <= -Math.PI) d += Math.PI * 2;
  return d;
}

// ---------------------------------------------------------------------------
// Rectangles
// ---------------------------------------------------------------------------

export const rect = (x: number, y: number, w: number, h: number): Rect => ({ x, y, w, h });

export const rectCenter = (r: Rect): Vec => ({ x: r.x + r.w / 2, y: r.y + r.h / 2 });
export const rectRight = (r: Rect): number => r.x + r.w;
export const rectBottom = (r: Rect): number => r.y + r.h;

export function rectFromPoints(a: Vec, b: Vec): Rect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) };
}

/** Normalises negative width/height, which drag gestures produce freely. */
export function rectNormalize(r: Rect): Rect {
  return {
    x: r.w < 0 ? r.x + r.w : r.x,
    y: r.h < 0 ? r.y + r.h : r.y,
    w: Math.abs(r.w),
    h: Math.abs(r.h),
  };
}

export function rectContainsPoint(r: Rect, p: Vec): boolean {
  return p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
}

export function rectContainsRect(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.w <= outer.x + outer.w &&
    inner.y + inner.h <= outer.y + outer.h
  );
}

export function rectIntersects(a: Rect, b: Rect): boolean {
  return !(
    a.x + a.w < b.x ||
    b.x + b.w < a.x ||
    a.y + a.h < b.y ||
    b.y + b.h < a.y
  );
}

export function rectUnion(a: Rect, b: Rect): Rect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return {
    x,
    y,
    w: Math.max(a.x + a.w, b.x + b.w) - x,
    h: Math.max(a.y + a.h, b.y + b.h) - y,
  };
}

export function rectExpand(r: Rect, by: number): Rect {
  return { x: r.x - by, y: r.y - by, w: r.w + by * 2, h: r.h + by * 2 };
}

export function boundsOfPoints(points: readonly Vec[]): Rect {
  if (points.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

export function boundsOfRects(rects: readonly Rect[]): Rect {
  if (rects.length === 0) return { x: 0, y: 0, w: 0, h: 0 };
  let out = rects[0]!;
  for (let i = 1; i < rects.length; i++) out = rectUnion(out, rects[i]!);
  return out;
}

/** The four corners of a rect after rotation about its centre. */
export function rectCorners(r: Rect, rotation = 0): [Vec, Vec, Vec, Vec] {
  const c = rectCenter(r);
  const p: [Vec, Vec, Vec, Vec] = [
    { x: r.x, y: r.y },
    { x: r.x + r.w, y: r.y },
    { x: r.x + r.w, y: r.y + r.h },
    { x: r.x, y: r.y + r.h },
  ];
  if (rotation === 0) return p;
  return [
    vRotate(p[0], c, rotation),
    vRotate(p[1], c, rotation),
    vRotate(p[2], c, rotation),
    vRotate(p[3], c, rotation),
  ];
}

/** Axis-aligned bounding box of a rotated rect. */
export function rotatedBounds(r: Rect, rotation: number): Rect {
  if (rotation === 0) return r;
  return boundsOfPoints(rectCorners(r, rotation));
}

// ---------------------------------------------------------------------------
// Lines, segments, polygons
// ---------------------------------------------------------------------------

export function closestPointOnSegment(a: Vec, b: Vec, p: Vec): Vec {
  const ab = vSub(b, a);
  const len2 = vDot(ab, ab);
  if (len2 < EPSILON) return { x: a.x, y: a.y };
  const t = clamp(vDot(vSub(p, a), ab) / len2, 0, 1);
  return { x: a.x + ab.x * t, y: a.y + ab.y * t };
}

export function distToSegment(a: Vec, b: Vec, p: Vec): number {
  return vDist(p, closestPointOnSegment(a, b, p));
}

export function distToPolyline(points: readonly Vec[], p: Vec): number {
  if (points.length === 0) return Infinity;
  if (points.length === 1) return vDist(points[0]!, p);
  let best = Infinity;
  for (let i = 1; i < points.length; i++) {
    const d = distToSegment(points[i - 1]!, points[i]!, p);
    if (d < best) best = d;
  }
  return best;
}

export function segmentsIntersect(a1: Vec, a2: Vec, b1: Vec, b2: Vec): boolean {
  const d1 = vCross(vSub(a2, a1), vSub(b1, a1));
  const d2 = vCross(vSub(a2, a1), vSub(b2, a1));
  const d3 = vCross(vSub(b2, b1), vSub(a1, b1));
  const d4 = vCross(vSub(b2, b1), vSub(a2, b1));
  return ((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0));
}

/** Even-odd winding test. */
export function pointInPolygon(poly: readonly Vec[], p: Vec): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const pi = poly[i]!;
    const pj = poly[j]!;
    const intersects =
      pi.y > p.y !== pj.y > p.y &&
      p.x < ((pj.x - pi.x) * (p.y - pi.y)) / (pj.y - pi.y || EPSILON) + pi.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

export function polylineIntersectsRect(points: readonly Vec[], r: Rect): boolean {
  for (const p of points) if (rectContainsPoint(r, p)) return true;
  const [c0, c1, c2, c3] = rectCorners(r);
  const edges: [Vec, Vec][] = [
    [c0, c1],
    [c1, c2],
    [c2, c3],
    [c3, c0],
  ];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    for (const [e1, e2] of edges) if (segmentsIntersect(a, b, e1, e2)) return true;
  }
  return false;
}

/**
 * Where does the ray from the centre of a rect towards `target` leave the rect?
 * Used to park connector endpoints on the border of the node they attach to.
 */
export function rectBorderPointTowards(r: Rect, target: Vec, rotation = 0): Vec {
  const c = rectCenter(r);
  // Work in the un-rotated frame, then rotate the answer back.
  const local = rotation === 0 ? target : vRotate(target, c, -rotation);
  const dx = local.x - c.x;
  const dy = local.y - c.y;
  if (Math.abs(dx) < EPSILON && Math.abs(dy) < EPSILON) return c;
  const halfW = r.w / 2;
  const halfH = r.h / 2;
  const scale = 1 / Math.max(Math.abs(dx) / (halfW || EPSILON), Math.abs(dy) / (halfH || EPSILON));
  const hit = { x: c.x + dx * scale, y: c.y + dy * scale };
  return rotation === 0 ? hit : vRotate(hit, c, rotation);
}

/** Same idea for ellipses, which need the parametric form instead. */
export function ellipseBorderPointTowards(r: Rect, target: Vec, rotation = 0): Vec {
  const c = rectCenter(r);
  const local = rotation === 0 ? target : vRotate(target, c, -rotation);
  const a = r.w / 2 || EPSILON;
  const b = r.h / 2 || EPSILON;
  const dx = local.x - c.x;
  const dy = local.y - c.y;
  const denom = Math.hypot(dx / a, dy / b);
  if (denom < EPSILON) return c;
  const hit = { x: c.x + dx / denom, y: c.y + dy / denom };
  return rotation === 0 ? hit : vRotate(hit, c, rotation);
}

/** Cubic bezier sampling, used for curved connectors and hit-testing them. */
export function cubicAt(p0: Vec, p1: Vec, p2: Vec, p3: Vec, t: number): Vec {
  const mt = 1 - t;
  const a = mt * mt * mt;
  const b = 3 * mt * mt * t;
  const c = 3 * mt * t * t;
  const d = t * t * t;
  return {
    x: a * p0.x + b * p1.x + c * p2.x + d * p3.x,
    y: a * p0.y + b * p1.y + c * p2.y + d * p3.y,
  };
}

export function sampleCubic(p0: Vec, p1: Vec, p2: Vec, p3: Vec, steps = 24): Vec[] {
  const out: Vec[] = [];
  for (let i = 0; i <= steps; i++) out.push(cubicAt(p0, p1, p2, p3, i / steps));
  return out;
}

/**
 * Ramer-Douglas-Peucker simplification. Freehand input arrives at pointer-event
 * rate (up to 240 Hz on a tablet); storing every sample would bloat the CRDT
 * document, so strokes are simplified once the pointer lifts.
 */
export function simplify(points: readonly Vec[], tolerance = 1): Vec[] {
  if (points.length < 3) return points.slice();
  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;
  const stack: [number, number][] = [[0, points.length - 1]];
  const tol2 = tolerance * tolerance;

  while (stack.length > 0) {
    const [first, last] = stack.pop()!;
    let maxDist2 = 0;
    let index = -1;
    const a = points[first]!;
    const b = points[last]!;
    for (let i = first + 1; i < last; i++) {
      const p = points[i]!;
      const c = closestPointOnSegment(a, b, p);
      const d2 = (p.x - c.x) ** 2 + (p.y - c.y) ** 2;
      if (d2 > maxDist2) {
        maxDist2 = d2;
        index = i;
      }
    }
    if (index !== -1 && maxDist2 > tol2) {
      keep[index] = 1;
      stack.push([first, index], [index, last]);
    }
  }

  const out: Vec[] = [];
  for (let i = 0; i < points.length; i++) if (keep[i]) out.push(points[i]!);
  return out;
}
